import { latLonToEN } from '@openlayers/ol-utils';
import { Ajax, Ajax2 } from '@shared/Ajax';
import { Events } from '@shared/Events';
import { FilesService } from '@shared/services/FilesService';
import { UsersService } from '@shared/services/UsersService';
import { LStorage } from '@shared/Storage';
import { Notify } from '@shared/ui-native/Notify';
import { transform as olTransform } from 'ol/proj';
import { mapRootStore, mapUI, mapWrapper } from './map';
import { DocumentsService } from '@shared/documents/DocumentsService';
import { ImagesService } from '@shared/images/ImagesService';
import { ModelService } from '@shared/models/model-service';

export async function processUrlParams() {
    const vars: any = utils.getUrlVars();
    console.log('processUrlParams:', vars);

    // EDIT IMAGE LOCATION
    if (vars.newImageLocation && $.isNumeric(vars.imageId)) {
        $('#newImageLocationTitle').show();

        mapWrapper.events.onClick(e => {
            const coord = mapWrapper.getInstance().getEventCoordinate(e);
            const [lon, lat] = olTransform(e.coordinate, 'EPSG:3857', 'EPSG:4326');

            location.href = `edit_image_${vars.imageId}.htm?lat=${lat}&lon=${lon}`;
        });
    }

    // VIEW SINGLE MODEL / DOC / IMAGE
    else if ($.isNumeric(vars.modelId)) {
        const rawFile = await FilesService.getDetails(vars.modelId);
        if (!rawFile) {
            Notify.warn(__('general.file_not_found'));
            return;
        }

        const filename = rawFile?.model?.name ?? rawFile?.image?.name;
        const ext = utils.getExtensionFromFilename(filename);

        if (/nfcl|pdf|txt|doc|xls|docx|xlsx/.test(ext)) {
            const doc = await DocumentsService.getDocumentById(rawFile.model.id);
            if (!doc.geographicPoint || (0 == doc.geographicPoint.lat && 0 == doc.geographicPoint.lon)) {
                Notify.warn(__('general.file_has_no_location'));
            }
            if (mapRootStore.documentsStore.fetchPromise) {
                mapRootStore.documentsStore.fetchPromise.then(() => {
                    mapWrapper.featureLayers.documents.openDocumentViewer(doc);
                });
            } else {
                mapWrapper.featureLayers.documents.openDocumentViewer(doc);
            }
        } else if (FilesService.isPhoto(filename)) {
            const img = await ImagesService.getImageById(rawFile.image.id);
            if (!img.geographicPoint || (0 == img.geographicPoint.lat && 0 == img.geographicPoint.lon)) {
                Notify.warn(__('general.file_has_no_location'));
            }
            if (mapRootStore.imagesStore.fetchPromise) {
                mapRootStore.imagesStore.fetchPromise.then(() => {
                    mapWrapper.featureLayers.images.openImageViewer(img);
                });
            } else {
                mapWrapper.featureLayers.images.openImageViewer(img);
            }
        } else {
            loadModel(vars.modelId, rawFile);
        }
    }

    // VIEW LOGPOINT(S), CSV ID's, legacy, before we had uuid's
    else if (vars.logpointId) {
        loadLogpointIds(vars.logpointId);
    }

    // VIEW LOGPOINT(S), BY FILTER
    else if (vars.action && vars.action === 'show-logpoints-by-filter') {
        const selectionRaw = LStorage.getItem('map-show-logpoints-filter');
        if (selectionRaw) {
            try {
                const selection = JSON.parse(selectionRaw);
                if (selection.projectId == kuura.getProjectId()) {
                    if (selection.items) {
                        // load csv ids
                        loadLogpointIds(selection.items);
                        // dont clear lstorage here, selection needs to be persisted inside saved-views
                    } else {
                        // load filter
                        Notify.success('Loading logpoints...');
                        Ajax({
                            url: 'api/1/logpoint/filter.json',
                            method: 'POST',
                            data: selection.filter + '&shape=ajax_logpoint&projectId=' + kuura.getProjectId(),
                        })
                            .done(resp => {
                                const loadedLogpointsFeatures = mapWrapper.featureLayers.logpoints.drawAll(resp.logpoints)?.features;
                                loadedLogpointsFeatures.forEach(l => l.set('forceShow', true));
                                mapWrapper.fitLogpointsLayer();
                                LStorage.setItem('map-show-logpoints-filter', {
                                    timestamp: Date.now(),
                                    projectId: kuura.getProjectId(),
                                    items: loadedLogpointsFeatures.map(f => f.get('data').id).join(','), // turn 'filter' into items so we can sae them into saved state
                                });
                            })
                            .fail(() => {
                                Notify.warn('Failed to load logpoints');
                            });
                    }
                }
            } catch {}
        } else {
            LStorage.removeItem('map-show-logpoints-filter');
        }
    }

    Events.on('load-image', (svt, data: IImage) => mapWrapper.featureLayers.images.openImageById(data.id));
    Events.on('load-document', (svt, data: IDocument) => mapWrapper.featureLayers.documents.openDocumentById(data.id));
    Events.on('load-model', (svt, id: number) => loadModel(id));
}

// Check and offer user to switch project is the model is located in another project
// they have links in pdf reports and emails
function loadModel(id: number, details?: IFileDetails) {
    FilesService.getProjectForFileId(id)
        .done(project => {
            if (!project) {
                return;
            }

            (details ? Promise.resolve(details) : FilesService.getDetails(id)).then(file => {
                if (project.id == kuura.getProjectId()) {
                    mapUI.mapPanels.activatePanelById('models');

                    // try to jump to model location before its loaded
                    ModelService.getDrawingStatus(file.model.uuid).then(info => {
                        if (info.bounding_box_available) {
                            const bb = info.bounding_box;
                            mapWrapper.fitExtent([bb.min_latitude, bb.min_longitude, bb.max_latitude, bb.max_longitude]);
                        }
                    });

                    // wait until full model is loaded
                    const promise = mapUI.mapPanels.panels.models.loadModelWithPreload(file.model?.headUuid);
                    if (promise) {
                        promise
                            .then(() => {
                                mapWrapper.fitFeatureById(id);
                                mapWrapper.featureLayers.logpoints.fetchAndDrawFile(id);
                                mapUI.mapPanels.focusFeatureInTree(id);
                            })
                            .catch(e => {
                                console.log('url load model:', e);
                                Notify.warn("Can't load model");
                            });
                    }
                } else {
                    const confirmMsg = kuura.getMessage('js_files.show_file_switch_project', project.name);
                    UsersService.changeProjectWithConfirm(project.id, confirmMsg);
                }
            });
        })
        .fail(() => Notify.error(kuura.getMessage('general.validation.access_not_allowed')));
}

async function loadLogpointIds(idsCsv: string) {
    const ids: number[] = idsCsv.split(',').map(id => parseInt(id, 10));

    if (Array.isArray(ids)) {
        const result = await mapWrapper.featureLayers.logpoints.fetchAndDrawMultiple(ids);
        if (result.features?.length) {
            result.features.forEach(f => f.set('forceShow', true));
            mapWrapper.fitLogpointsLayer();
        }
    }
}
